import { useEffect, useRef, useState } from 'react';
import env from 'environments';

import { buildBookingUrl } from './bookingUrl';
import strings from './strings';

/**
 * External coaching booking used while HubSpot Meetings is switched off
 * (hubspot-off-block-3 task 64, `VITE_HUBSPOT_MEETINGS_DISABLED`).
 *
 * Shows the surface's booking page in a plain iframe (prefilled with the
 * member's name, e-mail and phone) and one "I booked my call — continue"
 * button. The external calendar does not tell afy-ui about the booking, so the
 * button is what moves the member on: `onContinue` records the booking and
 * navigates. While it runs the button is disabled and a second click is
 * ignored; if it rejects, an error is shown and the member stays on the page.
 *
 * With no booking URL the member gets a support-contact message and no
 * button, since there is nothing to book.
 */
const ExternalBooking = ({ bookingUrl, prefill, onContinue, iframeHeight = 700 }) => {
  const [isPending, setIsPending] = useState(false);
  const [hasError, setHasError] = useState(false);
  const inFlight = useRef(false);
  const mounted = useRef(true);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  const src = buildBookingUrl(bookingUrl, prefill);

  if (!src) {
    return (
      <div role="status" data-testid="external-booking-unavailable" className="w-full py-6">
        <p className="font-semibold text-neutral-800 mb-2">{strings.unavailableTitle}</p>
        <p className="text-neutral-600">
          {strings.unavailableMessage({ email: env.SUPPORT_EMAIL, phone: env.SUPPORT_PHONE })}
        </p>
      </div>
    );
  }

  const handleContinue = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setIsPending(true);
    setHasError(false);
    try {
      await onContinue();
    } catch (error) {
      console.error('External booking confirmation failed:', error);
      if (mounted.current) setHasError(true);
    } finally {
      inFlight.current = false;
      if (mounted.current) setIsPending(false);
    }
  };

  return (
    <div className="flex flex-col w-full gap-4" data-testid="external-booking">
      <iframe
        title={strings.iframeTitle}
        src={src}
        width="100%"
        height={iframeHeight}
        style={{ border: 'none', width: '100%' }}
        data-testid="external-booking-iframe"
      />
      {hasError && (
        <p role="alert" className="text-error-500 text-sm">
          {strings.continueError}
        </p>
      )}
      <div className="flex justify-center">
        <button
          type="button"
          onClick={handleContinue}
          disabled={isPending}
          aria-busy={isPending}
          className="px-6 py-3 rounded-md bg-primary-500 text-white font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="external-booking-continue"
        >
          {isPending ? strings.continuePending : strings.continueButton}
        </button>
      </div>
    </div>
  );
};

export default ExternalBooking;
